import { Injectable, inject, signal, computed } from '@angular/core';
import { Route, Router } from '@angular/router';
import {
  Observable,
  BehaviorSubject,
  Subject,
  tap,
  catchError,
  of,
  map,
  finalize,
  shareReplay,
  switchMap,
} from 'rxjs';
import { ApiService } from './api.service';
import { CommonService } from './common.service';
import { GoogleIdentityService } from './google-identity.service';
import {
  COMPONENT_REGISTRY,
  LAZY_COMPONENT_LOADERS,
} from '../registry/component-registry'; 
// import { PageNotFoundComponent } from '../../pages/page-not-found/page-not-found.component';
import {
  UNPAID_PAYMENTS_PARENT,
  isUnpaidPaymentsProductComponent,
} from '../../pages/private/unpaid-payments-v2/unpaid-payments-route.config';
import { unpaidPaymentsProductAccessGuard } from '../../pages/private/unpaid-payments-v2/unpaid-payments-product-access.guard';
import { UnpaidPaymentsModalService } from '../../pages/private/unpaid-payments-v2/unpaid-payments-modal.service';
import { PageNotFoundComponent } from '../../pages/private/page-not-found/page-not-found.component';

export interface User {
  id: string;
  /** Primary key from backend; prefer this for APIs that expect user pid. */
  pid?: number;
  email: string;
  name: string;
  role: string;
  avatar?: string;
  [key: string]: any;
}

export interface AuthResponse {
  user: User;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiService = inject(ApiService);
  private commonService = inject(CommonService);
  private googleIdentity = inject(GoogleIdentityService);
  private router = inject(Router);
  private initialRoutesLoaded = false;

  private currentUserSubject = new BehaviorSubject<User | null>(null);
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  private isCheckingAuthSubject = new BehaviorSubject<boolean>(false);
  private signedOutSubject = new Subject<void>();
  private inFlightAuth: Observable<boolean> | null = null;

  // Tells every other open tab of this origin that the session is gone.
  private readonly sessionChannel =
    typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('prism-auth');

  /** Fires after local auth state is cleared, so session-bound services can let go. */
  signedOut$ = this.signedOutSubject.asObservable();

  currentUser$ = this.currentUserSubject.asObservable();
  isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  isCheckingAuth$ = this.isCheckingAuthSubject.asObservable();

  currentUser = signal<User | null>(null);
  isAuthenticated = signal<boolean>(false);
  isCheckingAuth = signal<boolean>(false);

  constructor() {
    this.currentUser$.subscribe((user) => this.currentUser.set(user));
    this.isAuthenticated$.subscribe((auth) => this.isAuthenticated.set(auth));
    this.isCheckingAuth$.subscribe((checking) =>
      this.isCheckingAuth.set(checking),
    );
    this.sessionChannel?.addEventListener('message', (event) => {
      if (event.data !== 'signed-out' || !this.isAuthenticatedSubject.value) return;
      this.clearUser();
      this.router.navigate(['/login']);
    });
  }

  authenticate(forceRefresh: boolean = false): Observable<boolean> {
    // A concurrent caller shares the check in progress instead of reading its stale `false`.
    if (this.inFlightAuth) {
      return this.inFlightAuth;
    }

    this.isCheckingAuthSubject.next(true);

    this.inFlightAuth = this.apiService
      .get<AuthResponse>(
        '/auth/authenticate',
        {},
        {
          showLoader: true,
          loaderMessage: 'Authenticating...',
          showErrorToaster: false,
          addAccessToken: true,
          addSecurityHeaders: true,
          showSuccessToaster: false,
        },
      )
      .pipe(
        tap((response) => {
          this.setUser(response.user);
          localStorage.setItem('user_profile', JSON.stringify(response.user));
          this.isAuthenticatedSubject.next(true);
        }),
        switchMap(() => {
          if (this.initialRoutesLoaded && !forceRefresh) {
            const stored = sessionStorage.getItem('dynamic_routes');
            if (stored) {
              return of({ routes: JSON.parse(stored), fromCache: true });
            }
          }
          return this.getDynamicRoutes().pipe(
            map(routes => ({ routes, fromCache: false }))
          );
        }),
        tap(({ routes, fromCache }) => {
          if (fromCache) {
            return;
          }
          sessionStorage.setItem('dynamic_routes', JSON.stringify(routes));
          this.buildDynamicRoutesFromApi(routes);
          this.initialRoutesLoaded = true;
        }),
        map(() => {
          return true;
        }),
        catchError((error) => {
          console.error('Authentication failed', error);
          this.clearUser();
          this.isAuthenticatedSubject.next(false);
          return of(false);
        }),
        finalize(() => {
          this.isCheckingAuthSubject.next(false);
          this.inFlightAuth = null;
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    return this.inFlightAuth;
  }

  getDynamicRoutes(): Observable<any[]> {
    return this.apiService
      .get<any[]>(
        '/auth/get-setting',
        {},
        {
          addAccessToken: true,
          addSecurityHeaders: true,
        },
      )
      .pipe(
        tap((response) => {
        }),
      );
  }

  /** Refreshes user settings and dynamic routes from the backend. */
  refreshPermissions(): Observable<any[]> {
    return this.getDynamicRoutes().pipe(
      tap((routes) => {
        sessionStorage.setItem('dynamic_routes', JSON.stringify(routes));
        this.buildDynamicRoutesFromApi(routes);
        this.initialRoutesLoaded = true;
      })
      );
  }

  /**
   * Silent variant of {@link refreshPermissions}: fetches the settings with no loader and applies
   * them only when they differ from what is already loaded, so an unchanged tab never rebuilds
   * routes or the sidebar. Emits true when something changed.
   */
  refreshPermissionsIfChanged(): Observable<boolean> {
    return this.getDynamicRoutes().pipe(
      map((routes) => {
        const fresh = JSON.stringify(routes);
        if (fresh === sessionStorage.getItem('dynamic_routes')) return false;
        sessionStorage.setItem('dynamic_routes', fresh);
        this.buildDynamicRoutesFromApi(routes);
        this.initialRoutesLoaded = true;
        this.leaveRevokedPage();
        return true;
      }),
    );
  }

  /**
   * After a silent settings change, moves the user off a page they no longer have: the new route
   * config only applies to the next navigation, so an open page would otherwise stay on screen.
   * Looks the URL up in the router config without navigating, so a page that is still allowed
   * is never re-created.
   */
  private leaveRevokedPage(): void {
    const segments = this.router.url.split(/[?#]/)[0].split('/').filter(Boolean);
    if (segments[0] !== 'app' || segments.length < 2) return;
    const app = this.router.config.find((r) => r.path === 'app');
    if (!app?.children) return;

    // Only dynamic (permission-driven) pages can be revoked; a static page is left alone.
    const rest = segments.slice(1);
    const isStatic = app.children.some((c) => !c.data?.['dynamic'] && c.path === rest[0]);
    if (isStatic || this.matchesRoute(app.children, rest)) return;

    this.router.navigateByUrl('/app');
  }

  private matchesRoute(children: Route[], segments: string[]): boolean {
    return children.some((child) => {
      if (child.path === undefined || child.path === '**' || child.redirectTo !== undefined) {
        return false;
      }
      const parts = child.path === '' ? [] : child.path.split('/');
      if (parts.length > segments.length) return false;
      if (!parts.every((p, i) => p.startsWith(':') || p === segments[i])) return false;
      const remaining = segments.slice(parts.length);
      if (remaining.length === 0) return true;
      return child.children ? this.matchesRoute(child.children, remaining) : false;
    });
  }

  setUser(user: User): void {
    this.currentUserSubject.next(user);
    this.isAuthenticatedSubject.next(true);
    sessionStorage.setItem('currentUser', JSON.stringify(user));
  }

  clearUser(): void {
    this.currentUserSubject.next(null);
    this.isAuthenticatedSubject.next(false);
    this.initialRoutesLoaded = false;

    sessionStorage.clear();

    localStorage.removeItem('selected_product_id');
    localStorage.removeItem('device_id');
    localStorage.removeItem('user_profile');
    this.googleIdentity.profilePhoto.forget();

    this.apiService.clearAccessToken();

    this.apiService.clearAllCookies();

    this.apiService.clearCache();

    this.signedOutSubject.next();
  }

  /**
   * Ends the session here and in every other tab. Only for a session that is really over
   * (sign-out, failed refresh, server revocation) — not for a tab that never had one.
   */
  endSession(): void {
    this.clearUser();
    this.sessionChannel?.postMessage('signed-out');
  }

  forgotPassword(email: string): Observable<any> {
    return this.apiService.post<any>(
      '/auth/forgot-password',
      { email },
      {
        showLoader: true,
        loaderMessage: 'Sending reset code...',
        showErrorToaster: true,
        showSuccessToaster: true,
        successMessage: 'If an account exists, a password reset code has been sent.',
      }
    );
  }

  setupPassword(payload: any): Observable<any> {
    return this.apiService.post<any>(
      '/auth/setup-password',
      payload,
      {
        showLoader: true,
        loaderMessage: 'Setting up your account...',
        showErrorToaster: true,
        showSuccessToaster: false,
      }
    );
  }

  resetPassword(payload: any): Observable<any> {
    return this.apiService.post<any>(
      '/auth/reset-password',
      payload,
      {
        showLoader: true,
        loaderMessage: 'Resetting your password...',
        showErrorToaster: true,
        showSuccessToaster: false,
      }
    );
  }

  logout(redirectToLogin = true): Observable<void> {
    // A deliberate sign-out is not a session interruption — drop any pending
    // deep link so the next login lands on /app, not wherever they left off.
    this.commonService.clearRedirectUrl();

    return this.apiService
      .post<void>(
        '/auth/logout',
        {},
        {
          showLoader: true,
          loaderMessage: 'Logging out...',
          showErrorToaster: false,
        },
      )
      .pipe(
        tap(() => {
          this.endSession();
          if (redirectToLogin) {
            this.router.navigate(['/login']);
          }
        }),
        catchError((error) => {
          console.error('Logout error:', error);
          this.endSession();
          if (redirectToLogin) {
            this.router.navigate(['/login']);
          }
          return of(undefined);
        }),
      );
  }

  getUser(): User | null {
    return this.currentUserSubject.value;
  }

  getUserId(): string | null {
    return this.currentUserSubject.value?.id || null;
  }

  /**
   * Numeric user pid from `/auth/authenticate` when present.
   * Falls back to parsing `id` only if it is a numeric string (legacy).
   */
  getUserPid(): number | null {
    const u = this.currentUserSubject.value;
    if (!u) return null;
    const raw = u.pid ?? u['pid'];
    if (typeof raw === 'number' && !Number.isNaN(raw)) return raw;
    if (typeof raw === 'string') {
      const n = parseInt(raw, 10);
      return Number.isNaN(n) ? null : n;
    }
    const idStr = u.id;
    if (typeof idStr === 'string' && /^\d+$/.test(idStr)) {
      return parseInt(idStr, 10);
    }
    return null;
  }

  getUserEmail(): string | null {
    return this.currentUserSubject.value?.email || null;
  }

  getUserRole(): string | null {
    return this.currentUserSubject.value?.role || null;
  }

  hasRole(role: string): boolean {
    return this.currentUserSubject.value?.role === role;
  }

  hasAnyRole(roles: string[]): boolean {
    const userRole = this.currentUserSubject.value?.role;
    return userRole ? roles.includes(userRole) : false;
  }

  //   buildDynamicRoutesFromApi(apiResponse: any[]) {

  //     const dynamicProducts: Route[] = [];

  //     apiResponse.forEach(product => {

  //       const productPath = this.clean(product.product.basePath);

  //       const productRoute: Route = {
  //         path: productPath,
  //         children: [],
  //         data: { dynamic: true }
  //       };

  //       product.routes?.forEach((route: any) => {

  //         const dashboardPath = this.clean(route.path);
  //         const component = COMPONENT_REGISTRY[route.componentName];

  //         const dashboardRoute: Route = {
  //           path: dashboardPath,
  //           data: { dynamic: true }
  //         };

  //         //  CASE 1: HAS SUB MENUS
  //         if (route.subMenus && route.subMenus.length > 0) {
  //           dashboardRoute.children = [];

  //           route.subMenus.forEach((sub: any) => {
  //             const subComponent = COMPONENT_REGISTRY[sub.componentName];

  //             if (!subComponent) {
  //               console.warn('❌ Component not found:', sub.componentName);
  //               return;
  //             }

  //             dashboardRoute.children!.push({
  //               path: this.clean(sub.path),
  //               component: subComponent,
  //               data: { dynamic: true }
  //             });
  //           });

  //         }
  //         // CASE 2: NO SUB MENUS → ATTACH COMPONENT
  //         else {
  //           if (!component) {
  //             console.warn('❌ Component not found:', route.componentName);
  //             return;
  //           }

  //           dashboardRoute.component = component;
  //       // ✅ CASE 1: HAS SUB MENUS
  //       // if (route.subMenus && route.subMenus.length > 0) {
  //       //   dashboardRoute.children = [];

  //       //   route.subMenus.forEach((sub: any) => {
  //       //     const subComponent = COMPONENT_REGISTRY[sub.componentName];

  //       //     if (!subComponent) {
  //       //       console.warn('❌ Component not found:', sub.componentName);
  //       //       return;
  //       //     }

  //       //     dashboardRoute.children!.push({
  //       //       path: this.clean(sub.path),
  //       //       component: subComponent,
  //       //       data: { dynamic: true }
  //       //     });
  //       //   });

  //       // }
  //       // ✅ CASE 1: HAS SUB MENUS
  // // if (route.subMenus && route.subMenus.length > 0) {

  // //   dashboardRoute.children = [];

  // //   // 🔥 DEFAULT REDIRECT (THIS FIXES BLANK PAGE)
  // //   dashboardRoute.children.push({
  // //     path: '',
  // //     redirectTo: this.clean(route.subMenus[0].path),
  // //     pathMatch: 'full'
  // //   });

  // //   route.subMenus.forEach((sub: any) => {
  // //     const subComponent = COMPONENT_REGISTRY[sub.componentName];

  // //     if (!subComponent) {
  // //       console.warn('❌ Component not found:', sub.componentName);
  // //       return;
  // //     }

  // //     dashboardRoute.children!.push({
  // //       path: this.clean(sub.path),
  // //       component: subComponent,
  // //       data: { dynamic: true }
  // //     });
  // //   });
  // // }
  // // ✅ CASE 1: HAS SUB MENUS
  // // if (route.subMenus && route.subMenus.length > 0) {

  // //   dashboardRoute.children = [];

  // //   // 🔥 ATTACH PARENT COMPONENT (THIS IS THE KEY)
  // //   if (component) {
  // //     dashboardRoute.component = component;
  // //   }

  // //   route.subMenus.forEach((sub: any) => {
  // //     const subComponent = COMPONENT_REGISTRY[sub.componentName];

  // //     if (!subComponent) {
  // //       console.warn('❌ Component not found:', sub.componentName);
  // //       return;
  // //     }

  // //     dashboardRoute.children!.push({
  // //       path: this.clean(sub.path),
  // //       component: subComponent,
  // //       data: { dynamic: true }
  // //     });
  // //   });
  // // }

  // // ✅ CASE 1: HAS SUB MENUS (FINAL FIX)
  // if (route.subMenus && route.subMenus.length > 0) {

  //   dashboardRoute.children = [];

  //   // parent component (PromptInfo)
  //   if (component) {
  //     dashboardRoute.component = component;
  //   }

  //   route.subMenus.forEach((sub: any) => {
  //     const subComponent = COMPONENT_REGISTRY[sub.componentName];

  //     if (!subComponent) {
  //       console.warn('❌ Component not found:', sub.componentName);
  //       return;
  //     }

  //     // 🔥 IMPORTANT: PUSH INTO dashboardRoute.children
  //     dashboardRoute.children!.push({
  //       path: this.clean(sub.path), // 'selection'
  //       component: subComponent,
  //       data: { dynamic: true }
  //     });
  //   });
  // }

  //       // ✅ CASE 2: NO SUB MENUS → ATTACH COMPONENT
  //       else {
  //         if (!component) {
  //           console.warn('❌ Component not found:', route.componentName);
  //           return;
  //         }

  //         productRoute.children!.push(dashboardRoute);

  //       });

  //       dynamicProducts.push(productRoute);
  //     });

  //     const updatedConfig = this.router.config.map(r => {
  //       if (r.path === 'app') {
  //         return {
  //           ...r,
  //           children: dynamicProducts
  //         };
  //       }
  //       return r;
  //     });

  //     this.router.resetConfig(updatedConfig);

  //     console.log(
  //       'FINAL ROUTESs',
  //       this.router.config.find(r => r.path === 'app')?.children
  //     );

  //     // Re-evaluate current URL
  //   //   Promise.resolve().then(() => {
  //   //     this.router.navigateByUrl(this.router.url);
  //   //  });
  //   }

  private resolveDynamicRoute(
    componentName: string,
  ): Pick<Route, 'component' | 'loadComponent'> | null {
    const lazyLoader = LAZY_COMPONENT_LOADERS[componentName];
    if (lazyLoader) {
      return { loadComponent: lazyLoader };
    }

    const component = COMPONENT_REGISTRY[componentName];
    if (!component) {
      // console.warn(
      //   `  ⚠️ Component NOT FOUND in COMPONENT_REGISTRY: ${componentName}`,
      // );
      return null;
    }

    if (!(component as { ɵcmp?: unknown }).ɵcmp) {
      // console.error(
      //   `  ❌ Component "${componentName}" is not a valid Angular component (missing ɵcmp). ` +
      //     'This usually indicates a circular import left the class undefined at runtime.',
      // );
      return null;
    }

    return { component };
  }

  buildDynamicRoutesFromApi(apiResponse: any[]) {
    const dynamicProducts: Route[] = [];

    apiResponse.forEach((product) => {
      const productPath = this.clean(product.product.basePath);
      // Which product this route subtree belongs to (e.g. "Credee", "Practina") —
      // components shared across products (like TrackingDemoComponent) read it from
      // ActivatedRoute data to scope their API calls. The parent is componentless, so
      // Angular's default 'emptyOnly' inheritance also delivers it to every child;
      // the child data literals repeat it for robustness.
      const productName = product.product?.name;

      const productRoute: Route = {
        path: productPath,
        children: [],
        data: { dynamic: true, productName },
      };

      product.routes?.forEach((route: any) => {
        const dashboardPath = this.clean(route.path);
        const resolvedRoute = this.resolveDynamicRoute(route.componentName);
        // console.log(`  🔸 Processing Route: ${route.name} (Path: ${dashboardPath}, ComponentName: ${route.componentName})`);

        const dashboardRoute: Route = {
          path: dashboardPath,
          data: { dynamic: true, productName },
        };

        //  CASE 1: HAS SUB MENUS
        if (route.subMenus && route.subMenus.length > 0) {
          dashboardRoute.children = [];

          if (resolvedRoute) {
            Object.assign(dashboardRoute, resolvedRoute);
          }

          const hasUnpaidProductChildren = route.subMenus.some((sub: any) =>
            isUnpaidPaymentsProductComponent(sub.componentName)
          );
          const isUnpaidPaymentsParentRoute =
            route.componentName === UNPAID_PAYMENTS_PARENT || hasUnpaidProductChildren;

          if (isUnpaidPaymentsParentRoute) {
            dashboardRoute.providers = [UnpaidPaymentsModalService];
          }

          // 🔥 NEW: If all subMenus are 'onlyPath: true', add a default redirect to the first subMenu
          const allOnlyPath = route.subMenus.every((sub: any) => sub.onlyPath);
          if (allOnlyPath && route.subMenus.length > 0) {
            dashboardRoute.children.push({
              path: '',
              redirectTo: this.clean(route.subMenus[0].path),
              pathMatch: 'full',
            });
          }

          route.subMenus.forEach((sub: any) => {
            const relativePath = sub.path.startsWith(route.path)
              ? sub.path.substring(route.path.length).replace(/^\/+/, '')
              : this.clean(sub.path);

            const subComponent = COMPONENT_REGISTRY[sub.componentName];

            const subResolved = this.resolveDynamicRoute(sub.componentName);
            if (!subResolved) {
              return;
            }

            const childRoute: Route = {
              path: relativePath,
              ...subResolved,
              data: { dynamic: true, productName },
              pathMatch: 'full',
            };

            if (isUnpaidPaymentsProductComponent(sub.componentName)) {
              childRoute.canActivate = [unpaidPaymentsProductAccessGuard];
              childRoute.data = { ...childRoute.data, unpaidProduct: true };
            }

            dashboardRoute.children!.push(childRoute);

            // 🔥 FIX: Inject detail route for Menu Access AFTER the base route to avoid collision
            if (
              sub.componentName === 'ManageRolesComponent' ||
              sub.componentName === 'RolesComponent' ||
              sub.path.includes('menu-roles')
            ) {
              const detailRoute = this.resolveDynamicRoute('RoleMenuAccessComponent');
              if (!detailRoute) {
                return;
              }

              // console.log(`      🔗 Injecting detail route for role management: ${sub.path}/:id`);
              dashboardRoute.children!.push({
                path: `${relativePath}/:id`,
                ...detailRoute,
                data: { dynamic: true },
              });
            }
          });

          // If parent has no component but has children, redirect to first child
          if (!dashboardRoute.component && dashboardRoute.children!.length > 0) {
            const firstChildPath = this.clean(route.subMenus[0].path);
            dashboardRoute.children!.unshift({
              path: '',
              pathMatch: 'full',
              redirectTo: firstChildPath,
            });
          }

          // 🔥 push dashboardRoute ONCE
          productRoute.children!.push(dashboardRoute);
        }

        // CASE 2: NO SUB MENUS
        else {
          // console.log(`    📄 Route has NO submenus`);
          if (!resolvedRoute) {
            // console.warn(`    ⚠️ Skipping route because component is missing`);
            return;
          }

          Object.assign(dashboardRoute, resolvedRoute);
          dashboardRoute.pathMatch = 'full';
          productRoute.children!.push(dashboardRoute);

          // 🔥 FIX: Manually inject detail route for Menu Access AFTER the base route to avoid collision
          if (
            route.componentName === 'ManageRolesComponent' ||
            route.componentName === 'RolesComponent' ||
            route.path.includes('menu-roles')
          ) {
            const detailRoute = this.resolveDynamicRoute('RoleMenuAccessComponent');
            if (detailRoute) {
              // console.log(`    🔗 Injecting detail route for role management: ${dashboardPath}/:id`);
            productRoute.children!.push({
              path: `${dashboardPath}/:id`,
                ...detailRoute,
              data: { dynamic: true },
            });
            }
          }
        }
      });

      productRoute.children!.push({
        path: '**',
        component: PageNotFoundComponent,
      });
      // If product route has no component but has children, redirect to first child
      if (!productRoute.component && productRoute.children!.length > 0) {
        const firstChildPath = productRoute.children![0].path!;
        productRoute.children!.unshift({
          path: '',
          pathMatch: 'full',
          redirectTo: firstChildPath,
        });
      }

      dynamicProducts.push(productRoute);
    });

    const dynamicPaths = new Set(dynamicProducts.map((dp) => dp.path));

    const updatedConfig = this.router.config.map((r) => {
      if (r.path === 'app') {
        const staticChildren = (r.children || []).filter(
          (child: Route) => !child.data?.['dynamic'] && !dynamicPaths.has(child.path) && child.path !== '**'
        );
        const wildcardRoute = (r.children || []).find((child: Route) => child.path === '**');
        const children = [...staticChildren, ...dynamicProducts];
        if (wildcardRoute) {
          children.push(wildcardRoute);
        }
        return {
          ...r,
          children,
        };
      }
      return r;
    });

    this.router.resetConfig(updatedConfig);
  }

  private clean(fullPath: string): string {
    return fullPath.split('/').filter(Boolean).pop()!;
  }

  restoreDynamicRoutesOnReload(): Observable<any[] | null> {
    // Restore cached routes synchronously first so navigation has something
    // to work with while fresh settings are being fetched.
    const stored = sessionStorage.getItem('dynamic_routes');
    if (stored) {
      try {
        this.buildDynamicRoutesFromApi(JSON.parse(stored));
      } catch {
        sessionStorage.removeItem('dynamic_routes');
      }
    }

    // Only re-fetch if user has an active session token (using getAccessToken() instead of localStorage)
    const hasSession = !!this.apiService.getAccessToken();
    if (!hasSession) {  
      return of(null);
    }

    // Always re-fetch settings on (hard) refresh so that any server-side
    // route changes are reflected immediately. Fall back to the cached
    // routes if the request fails (e.g. offline / not authenticated).
    return this.getDynamicRoutes().pipe(
      tap((routes) => {
        sessionStorage.setItem('dynamic_routes', JSON.stringify(routes));
        this.buildDynamicRoutesFromApi(routes);
        this.initialRoutesLoaded = true;
      }),
      catchError((error) => {
        console.error('Failed to reload dynamic routes on refresh', error);
        return of(null);
      }),
    );
  }
}





